import React, { useRef, useState, useEffect } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { ModelCard as ModelCardType } from './types';

interface VideoModelCardProps {
  model: ModelCardType;
  onClick?: (model: ModelCardType) => void;
}

const VideoModelCard: React.FC<VideoModelCardProps> = ({ model, onClick }) => {
  const descriptionRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    if (descriptionRef.current && model.description) {
      // Check if text is truncated by comparing scrollHeight with clientHeight
      // Note: This works even with webkit-line-clamp in most browsers
      const checkTruncation = () => {
        // Use a small delay to ensure layout is complete
        setTimeout(() => {
          if (descriptionRef.current) {
            const el = descriptionRef.current;
            // Create a clone to measure full height
            const clone = el.cloneNode(true) as HTMLElement;
            clone.style.cssText = window.getComputedStyle(el).cssText;
            clone.style.webkitLineClamp = 'none';
            clone.style.webkitBoxOrient = 'unset';
            clone.style.display = 'block';
            clone.style.overflow = 'visible';
            clone.style.height = 'auto';
            clone.style.visibility = 'hidden';
            clone.style.position = 'absolute';
            clone.style.width = el.offsetWidth + 'px';
            document.body.appendChild(clone);
            
            const fullHeight = clone.offsetHeight;
            const clampedHeight = el.offsetHeight;
            
            document.body.removeChild(clone);
            setIsTruncated(fullHeight > clampedHeight + 2);
          }
        }, 0);
      };
      
      checkTruncation();
    }
  }, [model.description]);

  return (
    <Box
      onClick={() => onClick?.(model)}
      sx={{
        width: '100%',
        maxWidth: '448px',
        height: '158px',
        position: 'relative',
        borderRadius: '16px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        padding: model.selected ? '2px' : '0px',
        background: model.selected
          ? 'linear-gradient(89.99deg, #1CB0C8 0.01%, #FF3DA3 99.99%)'
          : 'transparent',
        border: model.selected 
          ? 'none' 
          : '1px solid #2A2A2A',
        '&:hover': {
          transform: 'translateY(-2px)',
          padding: '2px',
          background: 'linear-gradient(89.99deg, #1CB0C8 0.01%, #FF3DA3 99.99%)',
          border: 'none',
          boxShadow: '0 4px 12px rgba(28, 176, 200, 0.3)'
        }
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          backgroundColor: '#161616',
          borderRadius: model.selected ? '14px' : '15px',
          padding: '16px',
          display: 'flex',
          flexDirection: 'row',
          gap: '10px',
          alignItems: 'center',
          overflow: 'hidden'
        }}
      >
        {/* Image Container with Play Button Overlay */}
        <Box
          sx={{
            width: '126px',
            height: '126px',
            flexShrink: 0,
            borderRadius: '8px',
            overflow: 'hidden',
            backgroundColor: '#2C2C2C',
            position: 'relative'
          }}
        >
          <img
            src={model.image}
            alt={model.title}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />

          {/* Play Button Overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.3)'
            }}
          >
            <Box
              sx={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(4px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid rgba(255,255,255,0.3)'
              }}
            >
              <PlayArrowIcon sx={{ color: 'white', fontSize: '20px' }} />
            </Box>
          </Box>
        </Box>

        {/* Text Content */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            height: '100%'
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Inter',
              fontSize: '22px',
              fontWeight: 700,
              color: '#FFFFFF',
              marginBottom: '8px',
              lineHeight: '1.2'
            }}
          >
            {model.title}
          </Typography>
          {isTruncated ? (
            <Tooltip 
              title={model.description}
              arrow
              placement="top"
            >
              <Typography
                ref={descriptionRef}
                sx={{
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#9CA3AF',
                  lineHeight: '1.4',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  cursor: 'help'
                }}
              >
                {model.description}
              </Typography>
            </Tooltip>
          ) : (
            <Typography
              ref={descriptionRef}
              sx={{
                fontFamily: 'Inter',
                fontSize: '14px',
                fontWeight: 400,
                color: '#9CA3AF',
                lineHeight: '1.4',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}
            >
              {model.description}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default VideoModelCard;

